import { jsx as _jsx } from "react/jsx-runtime";
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { AskInputUpsellPopover } from '#pplx/frontend/apps/ask/ask-input/ask-input-upsell-popover/components/AskInputUpsellPopover.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
const RATE_LIMIT_TITLE = (_jsx(FormattedMessage, { description: "Title for the search mode rate limit upsell popover", defaultMessage: "Monthly limit reached", id: "bb00b9b9c2" }));
export const AskInputSearchModeUpsellPopover = React.memo(function AskInputSearchModeUpsellPopover({ children, searchMode, gateReason, showMaxUpsell, subscriptionTier, onClick, }) {
    const action = useCallback((chunks) => (_jsx(ButtonPrimitive, { className: "inline cursor-pointer", onClick: onClick, children: _jsx(TextPrimitive, { size: "sm", color: "super", children: chunks }) })), [onClick]);
    const { title, description } = useMemo(() => {
        switch (gateReason) {
            case 'rate_limit':
                return getRateLimitContent(searchMode, action, showMaxUpsell);
            case 'auth':
                return getAuthContent(searchMode, action);
            case 'subscription':
                return getSubscriptionContent(searchMode, action);
            default:
                assertUnreachableValue(gateReason);
        }
    }, [searchMode, gateReason, showMaxUpsell, action]);
    const requiredTier = subscriptionTier === 'max' || showMaxUpsell ? 'max' : 'pro';
    return (_jsx(AskInputUpsellPopover, { title: title, description: description, requiredTier: requiredTier, children: children }));
});
function getAuthContent(searchMode, action) {
    switch (searchMode) {
        case SearchMode.RESEARCH:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for research mode auth upsell", defaultMessage: "Research any topic", id: "1b928acd39" })),
                description: (_jsx(FormattedMessage, { description: "Description for research mode auth upsell", defaultMessage: "Create in-depth reports, files, and apps by <action>signing in</action>", id: "2259bbfba8", values: { action } })),
            };
        case SearchMode.BROWSER_AGENT:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for browser agent mode auth upsell", defaultMessage: "Watch Comet work for you", id: "ec99c0924f" })),
                description: (_jsx(FormattedMessage, { description: "Description for browser agent mode auth upsell", defaultMessage: "Let Comet assist and perform actions for you by <action>signing in</action>", id: "8efee78a04", values: { action } })),
            };
        case SearchMode.STUDY:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for study mode auth upsell", defaultMessage: "Learn step by step", id: "7f64cbb055" })),
                description: (_jsx(FormattedMessage, { description: "Description for study mode auth upsell", defaultMessage: "Access interactive learning and quizzes by <action>signing in</action>", id: "7c255296b7", values: { action } })),
            };
        case SearchMode.AGENTIC_RESEARCH:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for model council mode auth upsell", defaultMessage: "Model council", id: "4408856651" })),
                description: (_jsx(FormattedMessage, { description: "Description for model council mode auth upsell", defaultMessage: "Use multiple AI models at once by <action>signing in</action>", id: "62a16ef845", values: { action } })),
            };
        default:
            return {
                title: (_jsx(FormattedMessage, { description: "Default title for search mode auth upsell", defaultMessage: "Research any topic", id: "7ba1181181" })),
                description: (_jsx(FormattedMessage, { description: "Default description for search mode auth upsell", defaultMessage: "Create in-depth reports, files, and apps by <action>signing in</action>", id: "b7eac51637", values: { action } })),
            };
    }
}
function getSubscriptionContent(searchMode, action) {
    switch (searchMode) {
        case SearchMode.RESEARCH:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for research mode subscription upsell", defaultMessage: "Research any topic", id: "24e929dca6" })),
                description: (_jsx(FormattedMessage, { description: "Description for research mode subscription upsell", defaultMessage: "Create in-depth reports, files, and apps by <action>upgrading your plan</action> today", id: "0ff4a88277", values: { action } })),
            };
        case SearchMode.BROWSER_AGENT:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for browser agent mode subscription upsell", defaultMessage: "Watch Comet work for you", id: "2521f4cc2d" })),
                description: (_jsx(FormattedMessage, { description: "Description for browser agent mode subscription upsell", defaultMessage: "Let Comet assist and perform actions for you by <action>upgrading your plan</action> today", id: "84a9440536", values: { action } })),
            };
        case SearchMode.STUDY:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for study mode subscription upsell", defaultMessage: "Learn step by step", id: "4c0db4798a" })),
                description: (_jsx(FormattedMessage, { description: "Description for study mode subscription upsell", defaultMessage: "Access interactive learning and quizzes by <action>upgrading your plan</action> today", id: "4846c08d5a", values: { action } })),
            };
        case SearchMode.AGENTIC_RESEARCH:
            return {
                title: (_jsx(FormattedMessage, { description: "Title for model council mode subscription upsell", defaultMessage: "Model council", id: "3a81c2282f" })),
                description: (_jsx(FormattedMessage, { description: "Description for model council mode subscription upsell", defaultMessage: "Use multiple AI models at once by <action>upgrading your plan</action> today", id: "555d8271c7", values: { action } })),
            };
        default:
            return {
                title: (_jsx(FormattedMessage, { description: "Default title for search mode subscription upsell", defaultMessage: "Research any topic", id: "515584c9f2" })),
                description: (_jsx(FormattedMessage, { description: "Default description for search mode subscription upsell", defaultMessage: "Create in-depth reports, files, and apps by <action>upgrading your plan</action> today", id: "9560edab8a", values: { action } })),
            };
    }
}
function getRateLimitContent(searchMode, action, showMaxUpsell) {
    if (!showMaxUpsell) {
        return {
            title: RATE_LIMIT_TITLE,
            description: (_jsx(FormattedMessage, { description: "Description for rate limit upsell when max upsell is not shown", defaultMessage: "You'll be able to create more once your limits reset", id: "9c2989fb16" })),
        };
    }
    switch (searchMode) {
        case SearchMode.RESEARCH:
            return {
                title: RATE_LIMIT_TITLE,
                description: (_jsx(FormattedMessage, { description: "Description for research mode rate limit upsell", defaultMessage: "Create more in-depth reports with our advanced research capabilities by <action>upgrading your plan</action> today", id: "0d654bd24f", values: { action } })),
            };
        default:
            return {
                title: RATE_LIMIT_TITLE,
                description: (_jsx(FormattedMessage, { description: "Default description for rate limit upsell", defaultMessage: "Create more in-depth reports with our advanced research capabilities by <action>upgrading your plan</action> today", id: "30e03e04a2", values: { action } })),
            };
    }
}
//# sourceMappingURL=AskInputSearchModeUpsellPopover.js.map